import { HttpResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { List } from "immutable";
import { map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { UserToken } from "../../../auth/user-token.model";
import { AutomapperService } from "../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../core/environment.tokens";
import { MessagingService } from "../../../core/messaging/messaging.service";
import { SeverityType } from "../../../core/messaging/severity-type.enum";
import { UserService } from "../../../core/user/user.service";
import { Autocomplete } from "../../../dynamic-forms/inputs/autocomplete/autocomplete.model";
import { CheckboxGroup } from "../../../dynamic-forms/inputs/checkbox-group/checkbox-group.model";
import { DynamicInput } from "../../../dynamic-forms/inputs/dynamic-input.model";
import { Radiobutton } from "../../../dynamic-forms/inputs/radiobutton/radiobutton.model";
import { SelectableInput } from "../../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../../dynamic-forms/inputs/textbox/textbox.model";
import { BulkAction } from "../../../shared/grid/bulk-actions/bulk-action.model";
import { GridColumnDefinition } from "../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../shared/grid/models/grid-configuration.model";
import { GridFilter } from "../../../shared/grid/models/grid-filter.model";
import { GridRequest } from "../../../shared/grid/models/grid-request.model";
import { ListItem } from "../../../shared/list/list-item";
import { ArrayHelper } from "../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { RetrievalPageService } from "../retrieval/retrieval-page/retrieval-page.service";
import { NewProjectComponent } from "./new-project/new-project.component";
import { ProjectType } from "./project-type.enum";
import { ProjectService } from "./project.service";

@Component({
  selector: "project-main",
  templateUrl: "./project.component.html",
  styleUrls: ["./project.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectComponent implements OnInit {
  private user: UserToken;
  projectStatsList = List<ListItem>();
  serverGridConfiguration = new GridConfiguration();
  serverRequest: GridRequest;
  serverGridSelection: any;
  isNewProjectView = false;
  threeDotActions: BulkAction[];
  selectedProjects: any[];
  private preselectedProjectIds: string[] = [];
  cloneProjectModalVisible = false;
  filename: string;
  readonly exportableProjectTypes: number[] = [ProjectType.RISK, ProjectType.IVA, ProjectType.HEDIS];
  readonly exportableProjectTypeNames: string[] = [
    ProjectType[ProjectType.RISK],
    ProjectType[ProjectType.IVA],
    ProjectType[ProjectType.HEDIS],
  ];
  @ViewChild("newProjectComponent", { static: false }) newProjectComponent: NewProjectComponent;

  get projectsInput(): CheckboxGroup {
    return this.getInput("ProjectIds") as CheckboxGroup;
  }
  set projectsInput(value: CheckboxGroup) {
    this.setInput("ProjectIds", value);
  }

  get projectStatusInput(): Radiobutton {
    return this.getInput("ProjectStatus") as Radiobutton;
  }

  set projectStatusInput(value: Radiobutton) {
    this.setInput("ProjectStatus", value);
  }

  get clientsInput(): CheckboxGroup {
    return this.getInput("ClientIds") as CheckboxGroup;
  }
  set clientsInput(value: CheckboxGroup) {
    this.setInput("ClientIds", value);
  }

  get productsInput(): Autocomplete {
    return this.getInput("ProductId") as Autocomplete;
  }
  set productsInput(value: Autocomplete) {
    this.setInput("ProductId", value);
  }

  get projectIdInput(): Textbox {
    return this.getInput("ProjectId") as Textbox;
  }

  get selectedProjectIds(): number[] {
    if (!ArrayHelper.isAvailable(this.selectedProjects)) {
      return [];
    }

    return this.selectedProjects.map(project => +project.projectId);
  }

  constructor(
    private service: ProjectService,
    private changeDetector: ChangeDetectorRef,
    private retrievalService: RetrievalPageService,
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private readonly automapper: AutomapperService,
    private messagingService: MessagingService,
    private readonly userService: UserService,
    private router: Router
  ) {  }

  ngOnInit() {
    this.user = this.userService.getUserToken();
    this.readPreselectedProjectIds();
    this.getProjectStats();

    this.retrievalService
      .getProjectList()
      .pipe(map(this.automapper.curryMany("LookupModel", "SelectableInput")))
      .subscribe(result => {
        this.projectsInput = { ...this.projectsInput, options: result } as any;
        this.applyPreselectedProjectFilter(result);
        this.changeDetector.markForCheck();
      });

    this.getClientOrgs();
    this.getProductTypes();
    this.createGrid();
  }

  getProjectStats(): void {
    this.service.getProjectStats().subscribe(result => {
      this.projectStatsList = this.assignAndNotify(result);
    });
  }

  trackByIndex(index) {
    return index;
  }

  assignAndNotify<T>(data: T[]): List<T> {
    this.changeDetector.markForCheck();
    const dataList = List(data);

    return dataList;
  }

  createGrid(): void {
    this.serverGridConfiguration.columns = [
      new GridColumnDefinition({ field: "projectName", header: "Project Name", routeUrl: "/project/:projectId" }),
      new GridColumnDefinition({ field: "projectId", header: "Project ID"}),
      new GridColumnDefinition({ field: "projectStatus", header: "Status" }),
      new GridColumnDefinition({ field: "clientName", header: "Group" }),
      new GridColumnDefinition({ field: "chaseCount", header: "Chases", isSortableColumn: false }),
      new GridColumnDefinition({ field: "userCount", header: "Users", routeUrl: "/user/users/:projectId", isSortableColumn: false }),
      new GridColumnDefinition({ field: "projectTypeName", header: "Type" }),
      new GridColumnDefinition({ field: "templateName", header: "Product" }),
      new GridColumnDefinition({ field: "projectDuration", header: "Duration", isSortableColumn: false }),
      new GridColumnDefinition({ field: "projectCreatedOnDate", header: "Created On"}),
    ];
    this.serverGridConfiguration.stateName = "project";
    this.serverRequest = new GridRequest({
      url: `${this.baseApiUrl}project/projectlist`,
      filters: [
        new GridFilter({
          input: new CheckboxGroup(),
          key: "ClientIds",
          name: "Group(s)",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "ProjectIds",
          name: "Projects",
        }),
        new GridFilter({
          input: new Radiobutton({
            options: [
              new SelectableInput({ text: "Active", value: "A" }),
              new SelectableInput({ text: "Inactive", value: "I" }),
            ],
          }),
          key: "ProjectStatus",
          name: "Status",
          value: "A",
        }),
        new GridFilter({
          input: new Autocomplete({
            placeholder: "Select Product...",
          }),
          key: "ProductId",
          name: "Products",
        }),
        new GridFilter({
          input: new Textbox(),
          key: "CallerComponent",
          name: "CallerComponent",
          value: "Project grid",
          show: false,
        }),
        new GridFilter({
          input: new Textbox(),
          key: "ProjectId",
          name: "Project ID",
        }),
      ],
    });
    this.threeDotActions = [
      new BulkAction({
        name: "CLONE PROJECT",
        action: this.promptForCloneProjectName,
      }),
    ];

    this.threeDotActions.push(
      new BulkAction({
        name: "EXPORT PROJECT",
        action: this.exportProject,
        isVisible: (rowData: any) => this.isExportableProjectType(rowData),
    }));

    // When arriving from the bulk project creation success page, pre-apply the Projects filter to the
    // newly created project ids so the grid shows only those projects. The grid persists its filters in
    // session storage (GRID_project) and restores them on load, so disable grid state here to keep the
    // cached filters from overriding the pre-applied project ids (without wiping the user's saved state).
    if (ArrayHelper.isAvailable(this.preselectedProjectIds)) {
      this.serverGridConfiguration.isStateDisabled = true;
      this.serverRequest.getFilter("ProjectIds").input.value =
        this.preselectedProjectIds.map(id => new SelectableInput({ text: id, value: id }));
    }

  }

  private readPreselectedProjectIds(): void {
    const navigationState = window.history.state;
    const ids = navigationState && Array.isArray(navigationState.projectIds) ? navigationState.projectIds : [];
    this.preselectedProjectIds = ids.map(id => `${id}`).filter(id => StringHelper.isAvailable(id));
  }

  private applyPreselectedProjectFilter(options: SelectableInput[]): void {
    if (!ArrayHelper.isAvailable(this.preselectedProjectIds) || this.serverRequest == null) {
      return;
    }

    const selected = (options || []).filter(option => option != null && this.preselectedProjectIds.includes(`${option.value}`));
    if (ArrayHelper.isAvailable(selected)) {
      this.serverRequest.getFilter("ProjectIds").inputValue = selected;
    }
  }

  private isExportableProjectType(rowData: any): boolean {
    const typeId = +(rowData && rowData.projectTypeId);
    if (!Number.isNaN(typeId) && typeId > 0) {
      return this.exportableProjectTypes.includes(typeId);
    }

    const typeName = (rowData && rowData.projectTypeName ? rowData.projectTypeName : "").toUpperCase();
    return this.exportableProjectTypeNames.some(name => typeName.includes(name.toUpperCase()));
  }

  exportProject = (rowData: any): void => {
    this.setSelectedProjects(rowData);
    this.service.exportProjectConfiguration(this.selectedProjects[0].projectId).subscribe(
      (response: HttpResponse<Blob>) => {
        this.saveExportedFile(response);
        this.messagingService.showToast("Project exported successfully.", SeverityType.SUCCESS);
        this.changeDetector.markForCheck();
      },
      () => {
        this.messagingService.showToast("Failed to export project.", SeverityType.ERROR);
        this.changeDetector.markForCheck();
      });
  }

  private saveExportedFile(response: HttpResponse<Blob>): void {
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(response.body);

    let fileName = "ProjectConfig.csv";
    const disposition = response.headers.get("content-disposition");
    if (disposition && disposition.indexOf("attachment") !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = matches[1].replace(/['"]/g, "");
      }
    }
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  promptForCloneProjectName = (rowData: any): void => {
    this.filename = "";
    this.setSelectedProjects(rowData);
    this.cloneProjectModalVisible = true;
  }

  cloneProject(): void {
    this.service.cloneProject(this.selectedProjects[0].projectId, this.filename).subscribe(
      (newProjectId: number) => {
        this.messagingService.showToast("Project is cloned.", SeverityType.SUCCESS);
        this.router.navigateByUrl(`project/${newProjectId}`);
        this.changeDetector.markForCheck();
      },
      () => {
        this.messagingService.showToast("Failed to clone project.", SeverityType.ERROR);
        this.changeDetector.markForCheck();
      });

    this.cloneProjectModalVisible = false;
  }

  get fileNameAvailable(): boolean {
    return StringHelper.isAvailable(this.filename);
  }

  private setSelectedProjects(rowData: any): void {
    this.selectedProjects = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
  }

  changeNewProjectDialog() {
    this.isNewProjectView = true;
  }

  createProject($event) {
    this.isNewProjectView = $event;
  }

  getClientOrgs() {
    this.service.getClientOrgsList()
      .pipe(map(this.automapper.curryMany("ClientOrgModel", "SelectableInput")))
      .subscribe(result => {
        this.newProjectComponent.clientOrgs.options = result;
        this.clientsInput = { ...this.clientsInput, options: result } as any;
        this.changeDetector.markForCheck();
      });
  }

  getProductTypes() {
    this.service.getProjectTemplateList()
      .pipe(
        map(this.automapper.curryMany("ProjectTemplateModel", "SelectableInput"))
      )
      .subscribe(result => {
        this.newProjectComponent.productTypes.options = result;
        this.productsInput = { ...this.productsInput, options: result } as any;
        this.changeDetector.markForCheck();
      });
  }

  getInput<T extends DynamicInput>(key: string): T {
    if (this.serverRequest == null) {
      return null;
    }

    return this.serverRequest.getInput<T>(key);
  }

  setInput<T extends DynamicInput>(key: string, value: T): void {
    if (this.serverRequest == null) {
      return null;
    }

    this.serverRequest.setInput<T>(key, value);
  }
}
